import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { passwordSchema } from "@/lib/passwordValidation";
import { supabase } from "@/integrations/supabase/client";
import TopShopRegistrationClosed from "./TopShopRegistrationClosed";
import { formatJamaicaTime } from "./topShopRegistration";
import { checkTopShopRegistrationOpenNow, TOP_SHOP_REGISTRATION_KEY, useTopShopRegistration } from "./useTopShopRegistration";
import { applicationParishSchema, TOP_SHOP_PARISHES, whatsappNumberSchema } from "./topShopApplicationValidation";

const TOPSHOP_HOSTS = ["topshopchallenge.com", "www.topshopchallenge.com"];
const IMPACT_OPTIONS = [
  ["none", "No damage"],
  ["minor", "Minor damage"],
  ["moderate", "Moderate damage"],
  ["severe", "Severe damage"],
  ["destroyed", "Destroyed"],
] as const;

const baseSchema = z.object({
  fullName: z.string().trim().max(120, "Full name must be 120 characters or fewer").optional(),
  email: z.string().trim().max(255, "Email must be 255 characters or fewer").optional(),
  password: z.string().optional(),
  businessName: z.string().trim().min(1, "Business name is required").max(120, "Business name must be 120 characters or fewer"),
  whatsappNumber: whatsappNumberSchema,
  parish: applicationParishSchema(),
  communityId: z.string().uuid().optional(),
  businessTypeId: z.string().uuid("Type of shop is required"),
  hurricaneImpactLevel: z.enum(["none", "minor", "moderate", "severe", "destroyed"], { required_error: "Please select the level of impact" }),
  hurricaneImpactDescription: z.string().trim().max(1000, "Please use 1,000 characters or fewer").optional(),
  whyJoin: z.string().trim().min(10, "Please tell us a little more").max(1000, "Please use 1,000 characters or fewer"),
  focusAreaIds: z.array(z.string().uuid()).min(1, "Choose at least one area"),
  privacyPolicy: z.boolean().optional(),
  sponsorConsent: z.literal(true, { errorMap: () => ({ message: "You must agree before submitting" }) }),
});

type FormValues = z.infer<typeof baseSchema>;

type ApplicationStatus = {
  created_at: string;
  status: string;
};

const statusLabels: Record<string, string> = {
  submitted: "Submitted",
  accepted: "Accepted",
  waitlisted: "Waitlisted",
  declined: "Declined",
  withdrawn: "Withdrawn",
  completed: "Completed",
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p role="alert" className="text-sm font-medium text-destructive">{message}</p>;
}

function ApplicationStatusCard({ application }: { application: ApplicationStatus }) {
  const formattedDate = new Intl.DateTimeFormat("en-JM", { dateStyle: "long" }).format(new Date(application.created_at));
  return (
    <div className="rounded-md border border-success/30 bg-success/5 p-6 sm:p-8">
      <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
      <h3 className="mt-4 text-2xl font-bold">Application received</h3>
      <p className="mt-2 text-muted-foreground">You applied on {formattedDate}. Status: <strong className="text-foreground">{statusLabels[application.status] ?? application.status}</strong></p>
      <Button asChild className="mt-6">
        <Link to="/topshop/application">Add or view photos</Link>
      </Button>
    </div>
  );
}

export type EditableTopShopApplication = {
  business_name: string;
  parish: string;
  business_type_id: string | null;
  community_id: string | null;
  hurricane_impact_level: string;
  hurricane_impact_description: string | null;
  focus_area_ids: string[] | null;
  why_join: string | null;
  whatsapp_number?: string | null;
};

type TopShopApplicationFormProps =
  | { mode?: "new" }
  | { mode: "edit"; application: EditableTopShopApplication; onDone?: () => void; onCancel?: () => void };

export default function TopShopApplicationForm(props: TopShopApplicationFormProps = {}) {
  const editMode = props.mode === "edit";
  const editApp = props.mode === "edit" ? props.application : null;
  const { user, loading: authLoading, signUp } = useAuth();
  const queryClient = useQueryClient();
  const [signupComplete, setSignupComplete] = useState<string | null>(null);
  const [closedDuringSubmit, setClosedDuringSubmit] = useState(false);
  const registration = useTopShopRegistration();
  const isTopShopHost = TOPSHOP_HOSTS.includes(window.location.hostname);
  const signedInMode = editMode || (Boolean(user) && !isTopShopHost);
  const signInHref = isTopShopHost ? "https://eabconnect.com/auth?redirect=/topshop/application" : "/auth?redirect=/topshop/application";

  const applicationSchema = useMemo(() => editMode
    ? (baseSchema.extend({ parish: applicationParishSchema(editApp?.parish), sponsorConsent: z.boolean().optional() }) as unknown as typeof baseSchema)
    : signedInMode ? baseSchema : baseSchema.extend({
    fullName: z.string().trim().min(2, "Name must be at least 2 characters").max(120, "Full name must be 120 characters or fewer"),
    email: z.string().trim().email("Invalid email address").max(255, "Email must be 255 characters or fewer"),
    password: passwordSchema,
    privacyPolicy: z.literal(true, { errorMap: () => ({ message: "You must agree to the Privacy Policy" }) }),
  }), [signedInMode, editMode, editApp?.parish]);

  const form = useForm<FormValues>({
    resolver: zodResolver(applicationSchema),
    defaultValues: editApp ? {
      fullName: "",
      email: "",
      password: "",
      businessName: editApp.business_name ?? "",
      parish: editApp.parish || undefined,
      whatsappNumber: editApp.whatsapp_number ?? "",
      businessTypeId: editApp.business_type_id ?? undefined,
      communityId: editApp.community_id ?? undefined,
      hurricaneImpactLevel: editApp.hurricane_impact_level as FormValues["hurricaneImpactLevel"],
      hurricaneImpactDescription: editApp.hurricane_impact_description ?? "",
      whyJoin: editApp.why_join ?? "",
      focusAreaIds: editApp.focus_area_ids ?? [],
      privacyPolicy: false,
      sponsorConsent: true,
    } : {
      fullName: "",
      email: "",
      password: "",
      businessName: "",
      whatsappNumber: "",
      communityId: undefined,
      hurricaneImpactDescription: "",
      whyJoin: "",
      focusAreaIds: [],
      privacyPolicy: false,
      sponsorConsent: false as true,
    },
  });

  const { data: program, isLoading: programLoading, error: programError } = useQuery({
    queryKey: ["top-shop", "program"],
    queryFn: async () => {
      const { data, error } = await supabase.from("programs").select("id, applications_open").eq("slug", "top-shop").single();
      if (error) throw error;
      return data;
    },
  });

  const { data: businessTypes = [] } = useQuery({
    queryKey: ["top-shop", "business-types", program?.id],
    enabled: Boolean(program?.id),
    queryFn: async () => {
      const { data, error } = await supabase.from("program_business_types").select("id, name").eq("program_id", program?.id ?? "").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: communities = [] } = useQuery({
    queryKey: ["top-shop", "communities", program?.id],
    enabled: Boolean(program?.id),
    queryFn: async () => {
      const { data, error } = await supabase.from("program_communities").select("id, name, parish, is_catch_all").eq("program_id", program?.id ?? "").eq("is_active", true).order("sort_order");
      if (error) throw error;
      const rows = data ?? [];
      const regular = rows.filter((c) => !c.is_catch_all);
      if (regular.length === 0) return [];
      return [...regular, ...rows.filter((c) => c.is_catch_all)];
    },
  });

  const { data: focusAreas = [] } = useQuery({
    queryKey: ["top-shop", "focus-areas", program?.id],
    enabled: Boolean(program?.id),
    queryFn: async () => {
      const { data, error } = await supabase.from("program_focus_areas").select("id, name").eq("program_id", program?.id ?? "").eq("is_active", true).order("sort_order");
      if (error) throw error;
      return data;
    },
  });

  const { data: existingApplication, isLoading: applicationLoading } = useQuery({
    queryKey: ["top-shop", "application", user?.id, program?.id],
    enabled: !editMode && signedInMode && Boolean(user?.id) && Boolean(program?.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("program_applications")
        .select("created_at, status")
        .eq("program_id", program?.id ?? "")
        .eq("user_id", user?.id ?? "")
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const onSubmit = async (values: FormValues) => {
    const application = {
      business_name: values.businessName,
      whatsapp_number: values.whatsappNumber,
      parish: values.parish,
      business_type_id: values.businessTypeId,
      community_id: values.communityId || null,
      hurricane_impact_level: values.hurricaneImpactLevel,
      hurricane_impact_description: values.hurricaneImpactDescription || null,
      focus_area_ids: values.focusAreaIds,
      why_join: values.whyJoin,
      sponsor_consent: true,
    };

    if (editMode) {
      const { sponsor_consent: _consent, ...p_data } = application;
      const { error } = await supabase.rpc("update_program_application" as any, { p_program_slug: "top-shop", p_data } as any);
      if (error) {
        toast.error(error.message.includes("application_locked")
          ? "Your application has already been reviewed and can no longer be edited."
          : error.message);
        return;
      }
      toast.success("Your application has been updated");
      await queryClient.invalidateQueries({ queryKey: ["top-shop"] });
      await queryClient.invalidateQueries({ queryKey: ["admin", "top-shop"] });
      if (props.mode === "edit") props.onDone?.();
      return;
    }

    let stillOpen = false;
    try {
      stillOpen = await checkTopShopRegistrationOpenNow();
    } catch {
      toast.error("We could not check whether registration is open. Please try again.");
      return;
    }
    if (!stillOpen) {
      setClosedDuringSubmit(true);
      queryClient.invalidateQueries({ queryKey: TOP_SHOP_REGISTRATION_KEY });
      return;
    }

    if (!signedInMode) {
      const { error } = await signUp(values.email ?? "", values.password ?? "", values.fullName ?? "", "entrepreneur", {
        extraMetadata: {
          program: "top-shop",
          source: isTopShopHost ? "topshopchallenge.com" : "eabconnect.com",
          application,
        },
        emailRedirectTo: "https://eabconnect.com/topshop",
      });
      if (error) {
        toast.error(error.message);
        return;
      }
      setSignupComplete(values.email ?? "");
      return;
    }

    const { data: newApplicationId, error } = await supabase.rpc("submit_program_application", { p_program_slug: "top-shop", p_data: application });
    if (error && error.message.includes("applications_closed")) {
      setClosedDuringSubmit(true);
      queryClient.invalidateQueries({ queryKey: TOP_SHOP_REGISTRATION_KEY });
      return;
    }
    if (error && !error.message.includes("already_applied")) {
      toast.error(error.message);
      return;
    }
    if (!error && newApplicationId) {
      // Best effort admin notice; never surface failures to the applicant.
      supabase.functions
        .invoke("notify-admin-topshop-application", { body: { application_id: newApplicationId } })
        .catch((e) => console.error("Top Shop admin notify failed", e));
    }
    await queryClient.invalidateQueries({ queryKey: ["top-shop", "application", user?.id, program?.id] });
  };

  if (authLoading || programLoading || applicationLoading || (!editMode && registration.isLoading)) {
    return <div className="flex min-h-48 items-center justify-center" aria-label="Loading application"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }
  if (programError || !program) {
    return <div className="rounded-md border border-destructive/30 bg-destructive/5 p-6 text-destructive">We could not load the application right now. Please try again later.</div>;
  }
  if (!editMode && existingApplication) return <ApplicationStatusCard application={existingApplication} />;
  if (!editMode && !signupComplete && (closedDuringSubmit || !registration.isOpen)) {
    return <TopShopRegistrationClosed message={registration.closedMessage} />;
  }
  if (signupComplete) {
    return (
      <div className="rounded-md border border-success/30 bg-success/5 p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-success" aria-hidden="true" />
        <h2 className="mt-5 text-3xl font-bold">Application received</h2>
        <p className="mx-auto mt-4 max-w-xl leading-7 text-muted-foreground">
          We sent a confirmation link to <strong className="text-foreground">{signupComplete}</strong>. Click it to confirm your address, then sign in at eabconnect.com with the email and password you just created to add photos of your shop. Your EAB Connect account is yours to keep, whatever the outcome of the challenge.
        </p>
      </div>
    );
  }

  const errors = form.formState.errors;
  return (
    <div className={editMode ? "" : "rounded-md border bg-background p-5 shadow-sm sm:p-8 lg:p-10"}>
      {editMode ? (
        <h2 className="text-xl font-bold">Edit your application</h2>
      ) : (
      <div className="max-w-2xl">
        <p className="font-bold text-warning">Apply to Top Shop</p>
        <h2 className="mt-2 text-3xl font-bold tracking-normal sm:text-4xl">Tell us about your business</h2>
        <p className="mt-3 text-muted-foreground">Applying creates your free EAB Connect account. Whether or not your shop is selected for the challenge, you keep access to training, tools and mentors on EAB Connect.</p>
        {registration.closeAt && (
          <p className="mt-3 font-semibold text-foreground">Applications close on {formatJamaicaTime(registration.closeAt, false)} Jamaica time</p>
        )}
        {!signedInMode ? (
          <p className="mt-3 text-muted-foreground">Already on EAB Connect? <a href={signInHref} className="font-semibold text-primary hover:underline">Sign in to apply</a></p>
        ) : (
          <p className="mt-3 text-muted-foreground">Applying as <strong className="text-foreground">{String(user?.user_metadata?.full_name ?? user?.email ?? "EAB Connect member")}</strong></p>
        )}
      </div>
      )}

      <form onSubmit={form.handleSubmit(onSubmit)} className="mt-10 space-y-10" noValidate>
        {!signedInMode && (
          <fieldset className="space-y-5">
            <legend className="text-xl font-bold">Create your EAB Connect account</legend>
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="topshop-full-name">Full name</Label>
                <Input id="topshop-full-name" autoComplete="name" {...form.register("fullName")} aria-invalid={Boolean(errors.fullName)} />
                <FieldError message={errors.fullName?.message} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="topshop-email">Email</Label>
                <Input id="topshop-email" type="email" autoComplete="email" {...form.register("email")} aria-invalid={Boolean(errors.email)} />
                <FieldError message={errors.email?.message} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="topshop-password">Password</Label>
              <Input id="topshop-password" type="password" autoComplete="new-password" {...form.register("password")} aria-invalid={Boolean(errors.password)} />
              <PasswordRequirements password={form.watch("password") ?? ""} />
              <FieldError message={errors.password?.message} />
            </div>
          </fieldset>
        )}

        <fieldset className="space-y-5">
          <legend className="text-xl font-bold">Your business</legend>
          <div className="space-y-2">
            <Label htmlFor="topshop-business-name">Business name</Label>
            <Input id="topshop-business-name" maxLength={120} {...form.register("businessName")} aria-invalid={Boolean(errors.businessName)} />
            <FieldError message={errors.businessName?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="topshop-whatsapp">WhatsApp number</Label>
            <Input id="topshop-whatsapp" type="tel" autoComplete="tel" maxLength={40} required {...form.register("whatsappNumber")} aria-invalid={Boolean(errors.whatsappNumber)} aria-describedby="topshop-whatsapp-help" />
            <p id="topshop-whatsapp-help" className="text-sm text-muted-foreground">We will use this to contact you about the challenge.</p>
            <FieldError message={errors.whatsappNumber?.message} />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="topshop-parish">Parish</Label>
              <Controller name="parish" control={form.control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="topshop-parish" aria-invalid={Boolean(errors.parish)}><SelectValue placeholder="Select parish" /></SelectTrigger>
                  <SelectContent>
                    {TOP_SHOP_PARISHES.map((parish) => <SelectItem key={parish} value={parish}>{parish}</SelectItem>)}
                    {editApp?.parish && !TOP_SHOP_PARISHES.some((parish) => parish === editApp.parish) && (
                      <SelectItem value={editApp.parish}>{editApp.parish}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )} />
              <FieldError message={errors.parish?.message} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="topshop-business-type">Type of shop</Label>
              <Controller name="businessTypeId" control={form.control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="topshop-business-type" aria-invalid={Boolean(errors.businessTypeId)}><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>{businessTypes.map((type) => <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>)}</SelectContent>
                </Select>
              )} />
              <FieldError message={errors.businessTypeId?.message} />
            </div>
          </div>
          {communities.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="topshop-community">Community (optional)</Label>
              <Controller name="communityId" control={form.control} render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="topshop-community"><SelectValue placeholder="Select community" /></SelectTrigger>
                  <SelectContent>{communities.map((community) => <SelectItem key={community.id} value={community.id}>{community.name}{community.parish ? `, ${community.parish}` : ""}</SelectItem>)}</SelectContent>
                </Select>
              )} />
              <p className="text-xs text-muted-foreground">Choose the community where your shop operates. If yours is not listed, choose My community is not listed.</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="topshop-why-join">Why do you want to be part of the Top Shop Challenge?</Label>
            <p className="text-sm text-muted-foreground">A few sentences is fine. Tell us what you hope to gain and what you would do with the support.</p>
            <Textarea id="topshop-why-join" maxLength={1000} rows={5} {...form.register("whyJoin")} aria-invalid={Boolean(errors.whyJoin)} />
            <p className="text-xs text-muted-foreground" aria-live="polite">{(form.watch("whyJoin") ?? "").length} / 1000</p>
            <FieldError message={errors.whyJoin?.message} />
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-xl font-bold">How did Hurricane Melissa affect your business?</legend>
          <Controller name="hurricaneImpactLevel" control={form.control} render={({ field }) => (
            <RadioGroup value={field.value} onValueChange={field.onChange} className="grid gap-3 sm:grid-cols-2">
              {IMPACT_OPTIONS.map(([value, label]) => (
                <div key={value} className="flex items-center gap-3 rounded-md border p-4">
                  <RadioGroupItem id={`impact-${value}`} value={value} />
                  <Label htmlFor={`impact-${value}`} className="cursor-pointer font-normal">{label}</Label>
                </div>
              ))}
            </RadioGroup>
          )} />
          <FieldError message={errors.hurricaneImpactLevel?.message} />
          <div className="space-y-2 pt-2">
            <Label htmlFor="topshop-impact-description">Tell us more (optional)</Label>
            <Textarea id="topshop-impact-description" maxLength={1000} rows={5} {...form.register("hurricaneImpactDescription")} aria-invalid={Boolean(errors.hurricaneImpactDescription)} />
            <FieldError message={errors.hurricaneImpactDescription?.message} />
          </div>
        </fieldset>

        <fieldset className="space-y-4">
          <legend className="text-xl font-bold">Which areas do you most want to grow in? Choose at least one.</legend>
          <Controller name="focusAreaIds" control={form.control} render={({ field }) => (
            <div className="grid gap-3 sm:grid-cols-2">
              {focusAreas.map((area) => {
                const checked = field.value.includes(area.id);
                return (
                  <div key={area.id} className="flex items-start gap-3 rounded-md border p-4">
                    <Checkbox id={`focus-${area.id}`} checked={checked} onCheckedChange={(next) => field.onChange(next === true ? [...field.value, area.id] : field.value.filter((id) => id !== area.id))} />
                    <Label htmlFor={`focus-${area.id}`} className="cursor-pointer font-normal leading-5">{area.name}</Label>
                  </div>
                );
              })}
            </div>
          )} />
          <FieldError message={errors.focusAreaIds?.message} />
        </fieldset>

        {!editMode && (
        <fieldset className="space-y-4">
          <legend className="text-xl font-bold">Agreements</legend>
          {!signedInMode && (
            <div className="flex items-start gap-3">
              <Controller name="privacyPolicy" control={form.control} render={({ field }) => <Checkbox id="topshop-privacy" checked={field.value} onCheckedChange={(value) => field.onChange(value === true)} />} />
              <div>
                <Label htmlFor="topshop-privacy" className="cursor-pointer font-normal">I agree to the <Link to="/privacy-policy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link></Label>
                <FieldError message={errors.privacyPolicy?.message} />
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Controller name="sponsorConsent" control={form.control} render={({ field }) => <Checkbox id="topshop-sponsor-consent" checked={field.value} onCheckedChange={(value) => field.onChange(value === true)} />} />
            <div>
              <Label htmlFor="topshop-sponsor-consent" className="cursor-pointer font-normal leading-6">I agree that Entrepreneurs Across Borders may share my name, business name, type of shop, location and challenge score with Top Shop Challenge sponsors.</Label>
              <FieldError message={errors.sponsorConsent?.message} />
            </div>
          </div>
        </fieldset>
        )}

        <div className="flex flex-wrap gap-3">
        <Button type="submit" size="lg" disabled={form.formState.isSubmitting} className="w-full bg-warning text-warning-foreground hover:bg-warning/90 sm:w-auto">
          {form.formState.isSubmitting && <Loader2 className="animate-spin" aria-hidden="true" />}
          {editMode ? "Save changes" : "Submit application"}
        </Button>
        {props.mode === "edit" && props.onCancel && (
          <Button type="button" size="lg" variant="outline" onClick={props.onCancel} disabled={form.formState.isSubmitting}>Cancel</Button>
        )}
        </div>
      </form>
    </div>
  );
}